/**
 * Descriptive-anchor links INTO /best-ai-music-video-generator from the
 * React pages of the music-video cluster (DirectorHome: /ai-music-video-
 * generator, /free-ai-music-video-generator, /suno-to-video, …) and the /vs/
 * comparison pages. The server-rendered bodies of those pages already carry
 * the exact-match anchor "best AI music video generators" (server/_core/
 * seo.ts renderInternalLinks); these give the rendered page a second,
 * descriptive anchor in the visitor's language.
 *
 * Kept tiny and separate from shared/bestMusicVideoTranslations.ts so the
 * generator page and the homepage don't bundle the whole listicle.
 *
 * `sentence` wraps the anchor in [[…]]. The sentence and the pill stay off
 * the homepage (#609: the homepage's copy does not talk about "best AI music
 * video generators"); the homepage links the list once, by its own name, in
 * the footer (BEST_MV_LIST_NAMES below).
 */
export type BestMvInboundLink = { sentence: string; pill: string };

export const BEST_MV_INBOUND_LINKS: Record<string, BestMvInboundLink> = {
  en: {
    sentence: "Still comparing tools? See the [[top AI music video generators compared]].",
    pill: "Top AI music video generators compared",
  },
  es: {
    sentence:
      "¿Sigues comparando herramientas? Mira los [[mejores generadores de videos musicales con IA comparados]].",
    pill: "Mejores generadores de videos musicales con IA comparados",
  },
  fr: {
    sentence:
      "Vous hésitez encore ? Voici [[les meilleurs générateurs de clips musicaux IA comparés]].",
    pill: "Meilleurs générateurs de clips musicaux IA comparés",
  },
  de: {
    sentence:
      "Du vergleichst noch? Hier sind die [[besten KI-Musikvideo-Generatoren im Vergleich]].",
    pill: "Die besten KI-Musikvideo-Generatoren im Vergleich",
  },
  it: {
    sentence:
      "Stai ancora confrontando gli strumenti? Guarda i [[migliori generatori di video musicali IA a confronto]].",
    pill: "Migliori generatori di video musicali IA a confronto",
  },
  pt: {
    sentence:
      "Ainda comparando ferramentas? Veja os [[melhores geradores de clipes musicais com IA comparados]].",
    pill: "Melhores geradores de clipes musicais com IA comparados",
  },
  nl: {
    sentence:
      "Nog aan het vergelijken? Bekijk de [[beste AI-muziekvideo-generators vergeleken]].",
    pill: "Beste AI-muziekvideo-generators vergeleken",
  },
  pl: {
    sentence:
      "Wciąż porównujesz narzędzia? Zobacz [[porównanie najlepszych generatorów teledysków AI]].",
    pill: "Porównanie najlepszych generatorów teledysków AI",
  },
  tr: {
    sentence:
      "Hâlâ araçları mı karşılaştırıyorsun? [[En iyi AI müzik video üreticilerinin karşılaştırmasına]] göz at.",
    pill: "En iyi AI müzik video üreticileri karşılaştırması",
  },
  ja: {
    sentence:
      "ツールを比較中ですか？[[おすすめのAIミュージックビデオ生成ツール比較]]をご覧ください。",
    pill: "おすすめのAIミュージックビデオ生成ツール比較",
  },
  ko: {
    sentence: "아직 비교 중이신가요? [[최고의 AI 뮤직비디오 생성기 비교]]를 확인해 보세요.",
    pill: "최고의 AI 뮤직비디오 생성기 비교",
  },
  zh: {
    sentence: "还在比较工具？看看[[最佳 AI 音乐视频生成器对比]]。",
    pill: "最佳 AI 音乐视频生成器对比",
  },
  id: {
    sentence:
      "Masih membandingkan alat? Lihat [[perbandingan generator video musik AI terbaik]].",
    pill: "Perbandingan generator video musik AI terbaik",
  },
  vi: {
    sentence:
      "Vẫn đang so sánh công cụ? Xem [[so sánh các trình tạo video nhạc AI tốt nhất]].",
    pill: "So sánh các trình tạo video nhạc AI tốt nhất",
  },
};

// Page languages (shared/pageLanguages.ts: Thai and Traditional Chinese, on
// /ai-music-video-generator only). Kept out of the map above, which is the 14
// site languages and nothing else; the comparison page these link to is the
// English one.
const PAGE_LANGUAGE_LINKS: Record<string, BestMvInboundLink> = {
  th: {
    sentence:
      "ยังเปรียบเทียบเครื่องมืออยู่ใช่ไหม ดู[[การเปรียบเทียบเครื่องมือสร้างมิวสิควิดีโอด้วย AI ชั้นนำ]]",
    pill: "เปรียบเทียบเครื่องมือสร้างมิวสิควิดีโอด้วย AI ชั้นนำ",
  },
  tw: {
    sentence: "還在比較工具？看看[[熱門 AI 音樂影片生成工具比較]]。",
    pill: "熱門 AI 音樂影片生成工具比較",
  },
};

/**
 * The list's own name in each site language: its h1, in sentence case in
 * English. The label of the homepage footer link (BestMvFooterLink in
 * LandingV2).
 *
 * Oct 14 2026: the homepage's crawler HTML linked the list ("Make a music
 * video too" block) but the hydrated homepage had no link to it at all; the
 * footer of the previous landing page had one and LandingV2 dropped it. The
 * year is in the label because the "… 2026" searches are the ones the list
 * should answer (the homepage itself ranks for the phrase without a year,
 * and stays as it is).
 *
 * seo.test.ts fails if a label and that language's h1 drift apart.
 */
export const BEST_MV_LIST_NAMES: Record<string, string> = {
  en: "Best AI music video generators in 2026",
  es: "Mejores generadores de videos musicales con IA en 2026",
  fr: "Les meilleurs générateurs de clips musicaux IA en 2026",
  de: "Die besten KI-Musikvideo-Generatoren 2026",
  it: "Migliori generatori di video musicali IA nel 2026",
  pt: "Melhores geradores de clipes musicais com IA em 2026",
  nl: "De beste AI-muziekvideo-generators in 2026",
  pl: "Najlepsze generatory teledysków AI w 2026",
  tr: "2026'da En İyi AI Müzik Video Üreticileri",
  ja: "2026年のおすすめAIミュージックビデオ生成ツール",
  ko: "2026년 AI 뮤직비디오 생성기 추천",
  zh: "2026 年最佳 AI 音乐视频生成器",
  id: "Generator Video Musik AI Terbaik di 2026",
  vi: "Trình tạo video nhạc AI tốt nhất 2026",
};

/** The list's name in `lang`; English for a language without one. */
export function bestMvListName(lang: string): string {
  return BEST_MV_LIST_NAMES[(lang || "en").slice(0, 2)] ?? BEST_MV_LIST_NAMES.en!;
}

export function getBestMvInboundLink(lang: string): BestMvInboundLink {
  const code = (lang || "en").slice(0, 2);
  return (
    BEST_MV_INBOUND_LINKS[code] ??
    PAGE_LANGUAGE_LINKS[code] ??
    BEST_MV_INBOUND_LINKS.en!
  );
}

/** Split "before [[anchor]] after" into its three parts. */
export function splitLinkSentence(sentence: string): {
  before: string;
  anchor: string;
  after: string;
} {
  const m = sentence.match(/^([\s\S]*?)\[\[([\s\S]+?)\]\]([\s\S]*)$/);
  if (!m) return { before: "", anchor: sentence, after: "" };
  return { before: m[1] ?? "", anchor: m[2] ?? "", after: m[3] ?? "" };
}
